import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  TrendingDown,
  School,
  Search,
  Person,
  MenuBook
} from '@mui/icons-material';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ViewMarks = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState([]);

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');

  // Load classes and exams on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [classesRes, examsRes] = await Promise.all([
          principalAPI.getClasses(),
          principalAPI.getExams()
        ]);
        setClasses(classesRes.data.classes || classesRes.data || []);
        setExams(examsRes.data.exams || examsRes.data || []);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load classes and exams');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Load students when class changes
  useEffect(() => {
    if (!selectedClass) {
      setStudents([]);
      setSelectedStudent('');
      return;
    }
    const fetchStudents = async () => {
      try {
        const res = await principalAPI.getStudents({ class_id: selectedClass });
        setStudents(res.data.students || res.data || []);
      } catch (err) {
        console.error('Error loading students:', err);
      }
    };
    fetchStudents();
    setSelectedStudent('');
  }, [selectedClass]);

  // Fetch marks when filters change
  useEffect(() => {
    const fetchMarks = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (selectedClass) params.class_id = selectedClass;
        if (selectedExam) params.exam_id = selectedExam;
        if (selectedStudent) params.student_id = selectedStudent;

        const res = await principalAPI.getMarks(params);
        setMarks(res.data.marks || []);
      } catch (err) {
        console.error('Error loading marks:', err);
        setError('Failed to load marks');
        setMarks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMarks();
  }, [selectedClass, selectedExam, selectedStudent]);

  // Filter marks by student (client-side fallback) and search term
  const filteredMarks = useMemo(() => {
    let result = marks;
    if (selectedStudent) {
      result = result.filter(m => m.student_id === selectedStudent);
    }
    if (!searchTerm) return result;
    const term = searchTerm.toLowerCase();
    return result.filter(m =>
      (m.students?.name || '').toLowerCase().includes(term) ||
      (m.students?.admission_number || '').toLowerCase().includes(term) ||
      (m.class_subjects?.subject_name || '').toLowerCase().includes(term)
    );
  }, [marks, searchTerm, selectedStudent]);

  // Summary stats
  const stats = useMemo(() => {
    if (filteredMarks.length === 0) {
      return { total: 0, average: 0, highest: 0, lowest: 0 };
    }
    const percentages = filteredMarks.map(m => (m.marks / (m.max_marks || 100)) * 100);
    return {
      total: filteredMarks.length,
      average: (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(1),
      highest: Math.max(...percentages).toFixed(1),
      lowest: Math.min(...percentages).toFixed(1)
    };
  }, [filteredMarks]);

  const getPercentageColor = (marks, maxMarks) => {
    const pct = (marks / (maxMarks || 100)) * 100;
    if (pct >= 80) return 'success';
    if (pct >= 60) return 'primary';
    if (pct >= 40) return 'warning';
    return 'error';
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Mobile card for each mark entry
  const MobileMarkCard = ({ mark, idx }) => {
    const pct = ((mark.marks / (mark.max_marks || 100)) * 100).toFixed(1);
    return (
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight="bold">
                {mark.students?.name || '-'}
              </Typography>
            </Box>
            <Chip
              label={`${pct}%`}
              size="small"
              color={getPercentageColor(mark.marks, mark.max_marks)}
            />
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1} mb={0.5}>
            <Chip
              icon={<MenuBook sx={{ fontSize: 14 }} />}
              label={mark.class_subjects?.subject_name || '-'}
              size="small"
              variant="outlined"
            />
            {mark.students?.admission_number && (
              <Chip
                label={mark.students.admission_number}
                size="small"
                variant="outlined"
                color="default"
              />
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="body2" color="text.secondary">
              {mark.marks}/{mark.max_marks || 100} marks
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mark.exams?.name || '-'}
            </Typography>
          </Box>
          {mark.remarks && mark.remarks !== '-' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Remarks: {mark.remarks}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
      <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 1.5 : 3 }}>
        {/* Page Title */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Assessment sx={{ fontSize: isMobile ? 26 : 32, color: 'primary.main' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
            Student Marks Report
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: isMobile ? 2 : 3, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  label="Class"
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <MenuItem value="">All Classes</MenuItem>
                  {classes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Exam</InputLabel>
                <Select
                  value={selectedExam}
                  label="Exam"
                  onChange={(e) => setSelectedExam(e.target.value)}
                >
                  <MenuItem value="">All Exams</MenuItem>
                  {exams.map((e) => (
                    <MenuItem key={e.id} value={e.id}>
                      {e.name}{e.academic_year ? ` (${e.academic_year})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!selectedClass}>
                <InputLabel>Student</InputLabel>
                <Select
                  value={selectedStudent}
                  label="Student"
                  onChange={(e) => setSelectedStudent(e.target.value)}
                >
                  <MenuItem value="">All Students</MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}{s.admission_number ? ` (${s.admission_number})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={isMobile ? 1 : 3} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 1.5 : 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : '0.8rem' }} fontWeight={500}>
                  Total Entries
                </Typography>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
            }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 1.5 : 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : '0.8rem' }} fontWeight={500}>
                  Average %
                </Typography>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                  {stats.average}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
            }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 1.5 : 2 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TrendingUp sx={{ fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.9)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : '0.8rem' }} fontWeight={500}>
                    Highest %
                  </Typography>
                </Box>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                  {stats.highest}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
            }}>
              <CardContent sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 1.5 : 2 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TrendingDown sx={{ fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.9)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : '0.8rem' }} fontWeight={500}>
                    Lowest %
                  </Typography>
                </Box>
                <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                  {stats.lowest}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by student name, admission number, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </Paper>

        {/* Marks Display */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : filteredMarks.length === 0 ? (
          <Paper>
            <Box textAlign="center" py={6}>
              <School sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No marks found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {marks.length === 0
                  ? 'Select filters above or marks have not been entered yet.'
                  : 'No results match your search.'}
              </Typography>
            </Box>
          </Paper>
        ) : isMobile ? (
          /* Mobile: Card-based layout */
          <Box>
            {filteredMarks.map((mark, idx) => (
              <MobileMarkCard key={mark.id || idx} mark={mark} idx={idx} />
            ))}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredMarks.length} of {marks.length} entries
              </Typography>
            </Box>
          </Box>
        ) : (
          /* Desktop: Table layout */
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Adm. No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Marks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Max</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">%</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Exam</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMarks.map((mark, idx) => {
                  const pct = ((mark.marks / (mark.max_marks || 100)) * 100).toFixed(1);
                  return (
                    <TableRow key={mark.id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{mark.students?.name || '-'}</TableCell>
                      <TableCell>{mark.students?.admission_number || '-'}</TableCell>
                      <TableCell>{mark.class_subjects?.subject_name || '-'}</TableCell>
                      <TableCell align="center">{mark.marks}</TableCell>
                      <TableCell align="center">{mark.max_marks || 100}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${pct}%`}
                          size="small"
                          color={getPercentageColor(mark.marks, mark.max_marks)}
                        />
                      </TableCell>
                      <TableCell>
                        {mark.exams?.name || '-'}
                      </TableCell>
                      <TableCell>{mark.remarks || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredMarks.length} of {marks.length} entries
              </Typography>
            </Box>
          </TableContainer>
        )}
      </Container>
  );
};

export default ViewMarks;
